import {
  useApiQuery,
  useApiMutation,
  useAuthenticatedMutation,
  type ApiQueryOptions,
  type ApiMutationOptions,
} from '@/hooks/useApi';
import { useQueryClient } from '@tanstack/react-query';
import utilityApi from '@/api/utilityApi';
import type { ApiResponse, PortOfficeModel } from '@/api/types';

type UtilityPayload = Record<string, unknown>;
type PortOfficesResponse = ApiResponse<PortOfficeModel[]>;

// Query keys for utility-related queries
export const UTILITY_KEYS = {
  portOffices: ['utility', 'portOffices'] as const,
  news: (page?: number, limit?: number) =>
    ['utility', 'news', { page, limit }] as const,
  newsById: (id: string) => ['utility', 'news', id] as const,
  tnvedCodes: (query: string) => ['utility', 'tnved', query] as const,
  exchangeRates: ['utility', 'exchangeRates'] as const,
};

/**
 * Hook for fetching port offices (public endpoint)
 */
export function usePortOffices(
  options: ApiQueryOptions<PortOfficesResponse> = {},
) {
  return useApiQuery<PortOfficesResponse>(
    UTILITY_KEYS.portOffices,
    () => utilityApi.getPortOffices(),
    options,
  );
}

/**
 * Hook for fetching news with pagination
 */
export function useNews(
  { page = 1, limit = 10 }: { page?: number; limit?: number } = {},
  options: ApiQueryOptions<ApiResponse<unknown>> = {},
) {
  return useApiQuery<ApiResponse<unknown>>(
    UTILITY_KEYS.news(page, limit),
    () => utilityApi.getNews({ page, limit }),
    options,
  );
}

/**
 * Hook for fetching a specific news article by ID
 */
export function useNewsItem(
  id: string,
  options: ApiQueryOptions<ApiResponse<unknown>> = {},
) {
  return useApiQuery<ApiResponse<unknown>>(
    UTILITY_KEYS.newsById(id),
    () => utilityApi.getNewsById(id),
    {
      enabled: !!id,
      ...options,
    },
  );
}

/**
 * Hook for searching TNVED codes
 */
export function useTnvedCodes(
  query = '',
  options: ApiQueryOptions<unknown> = {},
) {
  return useApiQuery<unknown>(
    UTILITY_KEYS.tnvedCodes(query),
    () => utilityApi.getTnvedCodes(query),
    {
      enabled: query.length > 0,
      staleTime: 5 * 60 * 1000, // 5 minutes
      ...options,
    },
  );
}

/**
 * Hook for fetching exchange rates
 */
export function useExchangeRates(options: ApiQueryOptions<unknown> = {}) {
  return useApiQuery<unknown>(
    UTILITY_KEYS.exchangeRates,
    () => utilityApi.getExchangeRates(),
    {
      staleTime: 60 * 60 * 1000, // 1 hour
      ...options,
    },
  );
}

/**
 * Hook for submitting "call me later" contact form
 */
export function useSubmitCallMeLater(
  options: ApiMutationOptions<ApiResponse<unknown>, UtilityPayload> = {},
) {
  return useApiMutation<ApiResponse<unknown>, UtilityPayload>(
    requestData => utilityApi.submitCallMeLater(requestData),
    {
      onSuccess: () => {
        // You can add toast notification here
      },
      ...options,
    },
  );
}

/**
 * Hook for uploading a file
 */
export function useUploadFile(
  options: ApiMutationOptions<
    ApiResponse<unknown>,
    { formData: FormData }
  > = {},
) {
  return useAuthenticatedMutation<ApiResponse<unknown>, { formData: FormData }>(
    ({ formData, token }) => utilityApi.uploadFile(formData, token),
    options,
  );
}

/**
 * Hook for downloading a file
 */
export function useDownloadFile(
  options: ApiMutationOptions<Blob, { id: string }> = {},
) {
  return useAuthenticatedMutation<Blob, { id: string }>(
    ({ id, token }) => utilityApi.downloadFile(id, token),
    options,
  );
}

/**
 * Hook for creating a news article (admin)
 */
export function useCreateNews(
  options: ApiMutationOptions<
    ApiResponse<unknown>,
    { newsData: UtilityPayload }
  > = {},
) {
  const queryClient = useQueryClient();

  return useAuthenticatedMutation<
    ApiResponse<unknown>,
    { newsData: UtilityPayload }
  >(({ newsData, token }) => utilityApi.createNews(newsData, token), {
    onSuccess: () => {
      // Invalidate all news queries
      queryClient.invalidateQueries({ queryKey: ['utility', 'news'] });
    },
    ...options,
  });
}

/**
 * Hook for updating a news article (admin)
 */
export function useUpdateNews(
  options: ApiMutationOptions<
    ApiResponse<unknown>,
    { id: string; newsData: UtilityPayload }
  > = {},
) {
  const queryClient = useQueryClient();

  return useAuthenticatedMutation<
    ApiResponse<unknown>,
    { id: string; newsData: UtilityPayload }
  >(({ id, newsData, token }) => utilityApi.updateNews(id, newsData, token), {
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: UTILITY_KEYS.newsById(variables.id),
      });
      queryClient.invalidateQueries({ queryKey: ['utility', 'news'] });
    },
    ...options,
  });
}

/**
 * Hook for deleting a news article (admin)
 */
export function useDeleteNews(
  options: ApiMutationOptions<ApiResponse<null>, { id: string }> = {},
) {
  const queryClient = useQueryClient();

  return useAuthenticatedMutation<ApiResponse<null>, { id: string }>(
    ({ id, token }) => utilityApi.deleteNews(id, token),
    {
      onSuccess: (_data, variables) => {
        queryClient.removeQueries({
          queryKey: UTILITY_KEYS.newsById(variables.id),
        });
        queryClient.invalidateQueries({ queryKey: ['utility', 'news'] });
      },
      ...options,
    },
  );
}
